import { LitElement, html, css, TemplateResult, svg } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { nothing } from 'lit-html';
import MGPlayerCore from '@meteogroup/player-core';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyageOLMap } from './voyage-ol-map';
import { VesselPositionModel } from '../../../src/models/vessel-position-model';
import { UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { VesselsTrackingModel } from '../../../src/models/vessels-tracking-model';
import { VesselRs1345Model } from '../../../src/models/vessel-rs1345-model';
import { RouteEditModel } from '../../../src/models/route-edit-model';
import { WarningEntry } from './voyage-weather-warning';
import { RulerLayer } from '../../../src/maplayers/ruler-layer';
import { VoyageStyle, voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { Coordinate } from 'ol/coordinate';
import { Layer } from 'ol/layer';
import { MapBrowserEvent } from 'ol';
import { ListenerFunction } from 'ol/events';
import { VoyageLayers } from '../../../src/maplayers/voyage-layers';
import { FeatureLike } from 'ol/Feature';
import { Pixel } from 'ol/pixel';
import { Source } from 'ol/source';
import { Geometry } from 'ol/geom';
import { elementCloseManager } from '../../../src/utils/element-close-manager';
import { vesselColors } from '../../../src/utils/fleet-point-icon-utils';
import { VesselCategories } from '../../../src/services/types/position-service.types';
import './voyage-ol-map';
import '../../voyage-tooltip/src/voyage-tooltip-proxy'
import './voyage-weather-warning';
import './voy-map-wp-info';
import { VoyageShoreContextModel } from '../../../src/models/voyage-shore-context-model';

const isVessel = (<any>window)["appenv"].vesselIMONumber !== undefined;
const _PROJECTION = (<any>window)["appenv"].projection;

export enum MapView {
    AdaptView = 'adapt-view',
}

export type TooltipSource = "Sailing directions" | "Spot weather" | "Nav areas" | "Whale zones" | "Marpol Areas" | "ECA/SECA Areas" | "Navtex stations" | "Ballast Water Management" | 'Restricted Areas' | "Security Zones"
    | "Terminal Geofences";

interface TooltipText {
    renderLine: () => TemplateResult | null,
    address?: string
}

@customElement('voyage-map')
export class VoyageMap extends LitElement
{
    @property({attribute: false}) followVessel: boolean = false;
    @property({attribute: false}) toggleRuler: boolean = false;
    @property({attribute: false}) showLegend: boolean = false;
    @property({attribute: false}) toggleTimeLine: any = false;

    routeLayers: Layer<Source>[] = [];

    constructor()
    {
        super();

        this._onVesselPositionUpdated = this._onVesselPositionUpdated.bind(this);
        this._onPositionsOrImoChanged = this._onPositionsOrImoChanged.bind(this);
        this._onWaypointSelected = this._onWaypointSelected.bind(this);
        this._onPointerMove = this._onPointerMove.bind(this);
        this._toggleRuler = this._toggleRuler.bind(this);
        this._toggleTimeLine = this._toggleTimeLine.bind(this);
        this._stylesChanged = this._stylesChanged.bind(this);
        this._moveTooltip = this._moveTooltip.bind(this);
        this._setLockTootlipOnLayerChange = this._setLockTootlipOnLayerChange.bind(this);
        this._renderControls = this._renderControls.bind(this);
        this._toggleFollowVessel = this._toggleFollowVessel.bind(this)
        localStorage.setItem("timeLineView",this.toggleTimeLine );
    }

    setTooltipText(source: TooltipSource, tooltip: TooltipText[]) {
            this.tooltipTextMap.set(source, tooltip);
            this.requestUpdate();
    }

    removeTooltipText(source: TooltipSource) {
        this.tooltipTextMap.delete(source);
        this.requestUpdate();
    }

    get vesselInTimePosition()
    {
        return this._vesselInTimePosition;
    }

    set vesselInTimePosition(position)
    {
        this._vesselInTimePosition = position;
        if (this._mapElement && this.followVessel)
        {
            this._centerMapOnVessel();
        }
    }

    static get styles() {
        return [
            voyageCursorStyles,
            css`
            :host {
                margin: 0;
                padding: 0;
            }

            #container {
                position: relative;
                width: 100%;
                height: 100%;
            }

            voyage-ol-map {
                height: 100%;
                width: 100%;
                cursor: auto;
            }

            voyage-ol-map[showcrosshair] {
                cursor: crosshair;
            }

            voyage-ol-map[pointer] {
                cursor: pointer;
            }

            #controls {
                position: absolute;
                display: flex;
                flex-direction: column;
                right: 16px;
                bottom: 15px;
            }

            button {
                background-color: var(--neutrals-grey-10, white);
                height: 32px;
                width: 32px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 3px;
                padding: 0px;
                margin: 2px;
                box-sizing: content-box;
                color: var(--neutrals-grey-01, black);
            }

            button[toggled] {
                background: var(--neutrals-grey-08, grey);
                border: 1px solid var(--neutrals-grey-04, grey);
            }

            svg {
                display: block;
                fill: currentColor;
                margin: auto;
                width: 16px;
                height: 16px;
            }

            #legend {
                position: absolute;
                display: grid;
                grid-template-columns: minmax(auto, 35px) 2fr;
                grid-gap: 5px 0px;
                background-color: var(--neutrals-grey-10, white);
                right: 16px;
                bottom: 205px;
                padding: 14px 16px 14px 5px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                box-sizing: content-box;
                color: var(--neutrals-grey-01, black);
                font-family: 'MaerskText';
                font-size: 14px;
            }

            #legend > .label {
                grid-column: 2;
            }

            #legend > .visual {
                grid-column: 1;
                display: flex;
                align-items: center;
            }
            .premium-icon svg{
                fill: #eac70d;
            }

            #legend svg {
                width: 16px;
                height: 16px;
            }

            #legend[hide] {
                display: none;
            }

            .innerButton {
                border: none;
                border-radius: none;
                margin: 0px;
            }

            .zoomControls {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin: 2px;
                background-color: var(--neutrals-grey-10, white);
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 3px;
            }

            .zoomControls svg{
                width: 10px;
            }

            .show-weather-tooltip svg{
                width: 15px;
            }

            .follow-vessel svg{
                width: 18px;
            }

            .divider {
                height: 1px;
                width: 100%;
                background-color: var(--neutrals-grey-06, grey);
            }

            #info-container {
                position: absolute;
                width: 361px;
                background-color: transparent;
            }

            voyage-weather-warning {
                width: 100%;
                pointer-events: none;
            }

            #spotWeatherTooltip {
                width: 450px;
                font-family: MaerskText;
                font-size: 12px;
                text-align: center;
                line-height: 20px;
                background: var(--neutrals-grey-10, white);
                border: 1px solid var(--neutrals-grey-01, black);
                position: absolute;
                display: inline-block;
                position: absolute;
                padding: 5px;
                color: var(--neutrals-grey-01, black);
                z-index: 11;
            }

            #spotWeatherTooltip[hidden] {
                display: none;
            }

            .tooltipHeader {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: bold;
                text-align: left;
                height: 20px;
            }

            .tooltipText {
                font-family: MaerskText;
                font-size: 10px;
                border: none;
                width: 100%;
                display: flex;
                flex-direction: column;
                text-align: left;
            }

            .navtex-station {
                font-family: MaerskText;
                font-size: 10px;
                cursor: pointer;
            }
            `
        ];
    }

    render() {
        const imo = Number(UiStateModel!.getValue("imo"));
        return html`
        <div id="spotWeatherTooltip" ?hidden=${!((this._cursorOnMap || this._lockTooltipMovement) && this.tooltipTextMap.size > 0)}>${this._generateTooltip()}</div>
        <div id="container">
        <voyage-ol-map id="map" @mapcoord=${() => this._onMapClick()} ?pointer=${this._cursorOnMap && this.tooltipTextMap.has("Navtex stations")} ?showcrosshair=${this._cursorOnMap && this.tooltipTextMap.size > 0 && !this.tooltipTextMap.has("Navtex stations")} @mousemove=${this._moveTooltip} @mouseover=${() => {this._cursorOnMap = true}} @mouseout=${() => {this._cursorOnMap = false}}></voyage-ol-map>
            <div ?hide=${!this.showLegend} id="legend">

            <span class="visual">
                ${svg`<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <line x1="0" y1="16" x2="64" y2="16"  stroke-width="8" stroke="#005487"/>
                </svg>`}
            </span>
            <span class="label">Plan</span>

            <span class="visual">
                ${svg`<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <line x1="0" y1="16" x2="64" y2="16"  stroke-width="4" stroke="#545454"/>
                </svg>`}
            </span>
            <span class="label">Past trail</span>

            <span class="visual">
                ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                    <line x1="16" y1="16" x2="48" y2="16" stroke-width="8" stroke="#878787"></line>
                    <line x1="32" y1="0" x2="32" y2="32" stroke-width="8" stroke="#878787"></line>
                </svg>`}
            </span>
            <span class="label">Navigational point</span>

            <span class="visual">
                ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="16" r="16" stroke-width="8" fill="transparent" stroke='black'/>
                </svg>`}
            </span>
            <span class="label">Waypoint</span>

            <span class="visual">
                ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="16" r="16" stroke-width="8" fill="${'black'}" stroke='black'/>
                </svg>`}
            </span>
            <span class="label">Stitched point</span>

            ${this._renderVesselTypes()}
            <span class="visual premium-icon">
                ${SvgIcons.customPremiumVessel}
            </span>
            <span class="label">Premium vessel</span>


            </div>
            <div id="controls">
                ${this._renderControls()}
            </div>
            <div id="info-container">
                <voy-map-wp-info .eta=${this._wpEta} .details=${this._wpDetails} .planIndex=${this._wpPlanIndex}>
                </voy-map-wp-info>
                <voyage-weather-warning .timestamp=${this._legStartTimestamp} .coordinates=${this._weatherCoordinates}
                                        .warnings=${this._weatherWarnings} .avoids=${this._weatherAvoids} .parametricRoll=${this._parametricRollWarning} .imo = ${imo}>
                </voyage-weather-warning>
            </div>
        </div>
        `;
    }

    getMap()
    {
        if (!this._mapElement)
        {
            return null;
        }
        return this._mapElement.getMap();
    }

    private _legendRenderers: (() => TemplateResult)[] = [];

    initMaps(player: MGPlayerCore, mapLayers: Layer<Source>[], voyageLayers?: VoyageLayers)
    {
        const styles = getComputedStyle(this);

        this._rulerLayer = new RulerLayer({ color: styles.getPropertyValue('--neutrals-grey-01') });
        this._mapElement!.initMaps(player, [...mapLayers, this._rulerLayer.layer!], voyageLayers);
        this._rulerLayer.assignMap(this._mapElement!.getMap()!);
        
        if (isVessel && VesselPositionModel!.position && VesselPositionModel!.position.latitude !== null && VesselPositionModel!.position.longitude !== null)
        {
            // Center on the position of the vessel initially
            this._vesselCurrentPosition = [VesselPositionModel!.position.longitude, VesselPositionModel!.position.latitude];
            this._mapElement!.centerOn(this._vesselCurrentPosition, true)
        }
        this._mapElement!.getMap()!.addEventListener('pointerdrag', () => { this.followVessel = false; return true; } );

        this._mapElement!.getMap()!.addEventListener('pointermove', <ListenerFunction>this._onPointerMove);

        this._legendRenderers = [];
        mapLayers.forEach(layer => {
            this._legendRenderers.push(...(layer.get('legendRenderers') || []));
        });
    }

    firstUpdated()
    {
        this._mapElement = this.shadowRoot!.querySelector('#map');
        this._infoContainerElement = this.shadowRoot!.querySelector("#info-container")
        this._spotWeatherTooltip = this.shadowRoot!.getElementById("spotWeatherTooltip")
    }

    connectedCallback()
    {
        super.connectedCallback();
        if (isVessel)
        {
            VesselPositionModel!.addEventListener('position-updated', this._onVesselPositionUpdated);
            RouteEditModel!.addEventListener('waypoint-selected', <EventListener>this._onWaypointSelected);
            UiStateModel!.addEventListener(`${UiStateModelParam.SelectedMapLayer}-change`, this._setLockTootlipOnLayerChange);
        }
        else
        {
            UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this._onPositionsOrImoChanged);
            VesselsTrackingModel!.addEventListener('positions-updated', this._onPositionsOrImoChanged);
            UiStateModel!.addEventListener(`${UiStateModelParam.SelectedMapLayer}-change`, this._setLockTootlipOnLayerChange);
        }
        VoyageStyle.addEventListener('styles-changed', this._stylesChanged);
        VesselsTrackingModel!.addEventListener("center-on-map", this._toggleFollowVessel);
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();
        if (isVessel)
        {
            VesselPositionModel!.removeEventListener('position-updated', this._onVesselPositionUpdated);
            RouteEditModel!.removeEventListener('waypoint-selected', <EventListener>this._onWaypointSelected);
            UiStateModel!.removeEventListener(`${UiStateModelParam.SelectedMapLayer}-change`, this._setLockTootlipOnLayerChange);
        }
        else
        {
            UiStateModel!.removeEventListener(`${UiStateModelParam.IMO}-change`, this._onPositionsOrImoChanged);
            VesselsTrackingModel!.removeEventListener('positions-updated', this._onPositionsOrImoChanged);
            UiStateModel!.removeEventListener(`${UiStateModelParam.SelectedMapLayer}-change`, this._setLockTootlipOnLayerChange);
        }
        VoyageStyle.removeEventListener('styles-changed', this._stylesChanged);
        VesselsTrackingModel!.removeEventListener("center-on-map", this._toggleFollowVessel);

    }

    @property({attribute: false}) private _parametricRollWarning: boolean = false;
    @property({attribute: false}) private _weatherWarnings: WarningEntry[] = [];
    @property({attribute: false}) private _weatherAvoids: WarningEntry[] = [];
    @property({attribute: false}) private _weatherCoordinates: Coordinate = [0,0];
    @property({attribute: false}) private _legStartTimestamp: string = "";
    @property({attribute: false}) private _cursorOnMap: boolean = false;
    @property({attribute: false}) private _wpEta: string = "";
    @property({attribute: false}) private _wpDetails: string = "";
    @property({attribute: false}) private _wpPlanIndex: number = -1;
    private _mapElement: VoyageOLMap|null = null;
    private _infoContainerElement: HTMLElement|null = null;
    private _vesselCurrentPosition: Coordinate|null = null;
    private _vesselInTimePosition: Coordinate|null = null;
    private _rulerLayer: RulerLayer|null = null;
    private _spotWeatherTooltip: HTMLElement|null = null;
    private tooltipTextMap: Map<TooltipSource, TooltipText[]> = new Map<TooltipSource, TooltipText[]>();
    private _lockTooltipMovement: boolean = false;
    private _moveCount: number = 0;

    private _renderVesselTypes() {
        return vesselColors.map(vesselCategory => {
            return html`
                <span class="visual">
                    ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="16" r="16" stroke-width="8" fill="${vesselCategory[1]}" stroke='black'/>
                    </svg>`}
                </span>
                <span class="label">${this._alignVesselCategoryNames(vesselCategory[0])}</span>
            `
        })
    }

    private _renderControls():TemplateResult {
        return html`
            <div>
                ${this._legendRenderers.length > 0 ? html`<button data-test="spotWeatherTooltip" ?toggled=${this.showLegend} class="show-weather-tooltip" @click=${() => this._toggleShowLegend()}>${SvgIcons.customLightQuestionCircle}</button>` : nothing}
                <voyage-tooltip-proxy text="Legend" html-content .positionPriority=${["left"]}></voyage-tooltip-proxy>
            </div>
            <div>
                <button data-test="ruler" ?toggled=${this.toggleRuler} @click=${() => this._toggleRuler()}>${SvgIcons.lightRulerHorizontal}</button>
                <voyage-tooltip-proxy text="Measure NM distance" html-content .positionPriority=${["left"]}></voyage-tooltip-proxy>
            </div>
            <div class="zoomControls">
                <button class="innerButton plus" data-test="zoomIn" @click=${ () => this._mapElement!.zoomIn() }>${SvgIcons.customPlus}</button>
                <div class="divider"></div>
                <button class="innerButton minus" data-test="zoomOut"  @click=${ () => this._mapElement!.zoomOut() }>${SvgIcons.customMinus}</button>
                <voyage-tooltip-proxy text="Zoom" html-content .positionPriority=${["left"]}></voyage-tooltip-proxy>
            </div>
            <div>
                <button  data-test="hide-timeline" ?toggled=${this.toggleTimeLine} @click=${() => this._toggleTimeLine()}>${SvgIcons.timeLineIcon}</button>
                <voyage-tooltip-proxy text=${this.toggleTimeLine ? "Hide timeline" : "Show timeline"} html-content .positionPriority=${["left"]}></voyage-tooltip-proxy>          
            </div>
        `
    }
    private _setLockTootlipOnLayerChange() {
        const currentLayer = UiStateModel!.getValue(UiStateModelParam.SelectedMapLayer);
        if (currentLayer !== "Sailing directions") {
            this._lockTooltipMovement = false;
        }
    }

    private _onMapClick() {
        if (this.tooltipTextMap.has("Navtex stations")) {
            const navtex = this.tooltipTextMap.get("Navtex stations");
            if (navtex) {
                VesselRs1345Model!.getHtml(navtex[0].address!); 
            }
        }
        
        if (this.tooltipTextMap.has("Sailing directions") && !this.tooltipTextMap.has("Spot weather")) {
            this._lockTooltipMovement = !this._lockTooltipMovement;
        } else {
            this._lockTooltipMovement = false;
        }
    }

    private _generateTooltip() {
        return Array.from(this.tooltipTextMap).map(item => {
            return html`<div class="tooltipHeader">
                            ${item[0]}
                        </div>
                        <div class="tooltipText">
                            ${item[1]!.map(line  => {
                                if (line.renderLine === null) {
                                    return ;
                                } else {
                                    return line.renderLine() // add support for custom buttons and urls
                                }
                            })}
                        </div>`
        });
    }

    private _moveTooltip(evt: MouseEvent) {
        if (this._lockTooltipMovement) {
            if (this.tooltipTextMap.has("Sailing directions")) {
                return;
            }
        }
        
        if (this.tooltipTextMap.size > 0) {
            const x = evt.offsetX+10;
            const y = evt.offsetY+75;
            this._spotWeatherTooltip!.style.left = x+'px';
            this._spotWeatherTooltip!.style.top = y+'px';
        }
    }

    private _stylesChanged()
    {
        if (this._rulerLayer)
        {
            const styles = getComputedStyle(this);
            this._rulerLayer.setColor(styles.getPropertyValue('--neutrals-grey-01'));
        }
    }

    private _toggleShowLegend() {
        this.showLegend = !this.showLegend;

        if (this.showLegend) {
            const closeLegend = () => {
                this.showLegend = false;
            }
            const togglableElement = this.shadowRoot!.querySelector("#legend");
            elementCloseManager.addElement(closeLegend, this, togglableElement!);
        }
    }

    private _toggleFollowVessel()
    {
        this.followVessel = !this.followVessel;
        if (this._mapElement && this.followVessel)
        {
            this._centerMapOnVessel();
        }
    }

    private _toggleRuler() {
        this.toggleRuler = !this.toggleRuler;
        //disable the focusing of left side on enabling the ruler, so user can drag from the left end
        UiStateModel!.setFocusPanel(!this.toggleRuler)

        if (this._mapElement && this.toggleRuler) {
            this._rulerLayer!.addInteractions({ruler: true});
        }

        if (this._mapElement && !this.toggleRuler) {
            this._rulerLayer!.removeInteractions();
        }
    }

    private _toggleTimeLine() {
        this.toggleTimeLine= !this.toggleTimeLine;
        localStorage.setItem("timeLineView",this.toggleTimeLine );
        VoyageShoreContextModel!.toggleTimeLineView();
    }

    private _onVesselPositionUpdated()
    {
        this._vesselCurrentPosition = [VesselPositionModel!.position!.longitude!, VesselPositionModel!.position!.latitude!];
        if (this._mapElement && this.followVessel)
        {
            this._centerMapOnVessel();
        }
    }

    private _onPositionsOrImoChanged()
    {
        const imo = UiStateModel!.getValue("imo");
        if (imo)
        {
            const vessel = VesselsTrackingModel!.filteredVesselPositions.get(Number(imo));
            if (vessel)
            {
                this._vesselCurrentPosition = [ vessel.longitude!, vessel.latitude! ];
            }
            else
            {
                this._vesselCurrentPosition = null;
            }
        }
        else
        {
            this._vesselCurrentPosition = null;
        }

        if (this._mapElement && this.followVessel)
        {
            this._centerMapOnVessel();
        }
    }

    private _onWaypointSelected(evt: CustomEvent) {
        const index = evt.detail.index;

        if (index >= 0 && RouteEditModel!.getWaypoints()[index].geometry) {
            let coordinate = RouteEditModel!.getWaypoints()[index].geometry.coordinates;
            if (coordinate)
            {
                this._mapElement!.centerOn(coordinate);
            }
        }
    }

    private _centerMapOnVessel()
    {
        let coordinate = this._vesselCurrentPosition;
        if (this._vesselInTimePosition)
        {
            // Use vessels position-in-time instead when set
            coordinate = this._vesselInTimePosition;
        }

        if (coordinate)
        {
            this._mapElement!.centerOn(coordinate);
        }
    }

    private _onPointerMove(evt: MapBrowserEvent<UIEvent>)
    {
     
        // Process every 5 move events only
        this._moveCount++;
        if (this._moveCount != 5) { return; }
        this._moveCount = 0;

        // Get features and determine whether we need to display something
        const map = this._mapElement!.getMap();
        const pixel = map!.getEventPixel(evt.originalEvent);
        const features = map!.getFeaturesAtPixel(pixel, { hitTolerance: 7.4, layerFilter    : (layer: Layer<Source>) => layer.getVisible()});

        this._processLegWarnings(features);
        this._processWPInfo(features);
        this._processOtherLayersInfo(features);

        this._infoContainerElement!.style.left = `${pixel[0]}px`;
        this._infoContainerElement!.style.top = `${pixel[1]}px`;

        return true;
    }

    private _processLegWarnings(features: FeatureLike[]): void
    {
        let foundFeature;
        for (const feature of features)
        {
            const warnings = feature.get("warnings");
           
            if (warnings && warnings.parametricRoll === true || (warnings && warnings.weather && (warnings.weather.warn.length > 0 || warnings.weather.avoid.length > 0)))
            {
                foundFeature = feature;
                break;
            }
        }

        if (foundFeature)
        {
            this._legStartTimestamp = foundFeature.get("legStartTime");
            this._weatherCoordinates = foundFeature.get("legStartCoords");
            this._weatherAvoids = foundFeature.get("warnings").weather.avoid;
            this._weatherWarnings = foundFeature.get("warnings").weather.warn;
            this._parametricRollWarning = foundFeature.get("warnings").parametricRoll;
        }
        else if (this._weatherAvoids.length || this._weatherWarnings.length || this._parametricRollWarning)
        {
            this._weatherAvoids = [];
            this._weatherWarnings = [];
            this._parametricRollWarning = false;
        }
    }

    private _processWPInfo(features: FeatureLike[]): void
    {
        let foundFeature;
        for (const feature of features)
        {
            if (feature.get("layer") === "ExecutionLayer" &&
                feature.getGeometry()?.getType() === "Point" &&
                (feature.get("type") === "Navigational" || feature.get("type") === "Hard") &&
                !feature.get("isPassed") && feature.get("timestamp") &&
                new Date(feature.get("timestamp")).getTime() > Date.now())
            {
                foundFeature = feature;
            }
        }

        if (foundFeature)
        {
            this._wpEta = foundFeature.get("timestamp") || "";
            this._wpDetails = foundFeature.get("name") || "";
            this._wpPlanIndex = foundFeature.get("planIndex");
        }
        else
        {
            this._wpEta = "";
            this._wpDetails = "";
            this._wpPlanIndex = -1;
        }
    }

    private _processOtherLayersInfo(features: FeatureLike[])
    {
        if (this._lockTooltipMovement) { // stop updating the tooltip if its locked
            return true;
        }

        const books = [];
        const chapters = [];
        const areas = [];
        const marpol = [];
        const ecaSeca = [];
        const navtexStations = [];
        const bwmZones = [];
        const restrictedAreas = [];
        const securityZones = [];
        const terminalGeofences = [];
        
        const whaleZonesText = new Set();
        for (const feature of features)
        {
            const hasFeaturesFromClusteredLayer = feature.get('features') ? feature.get('features').length > 0 : false;
            const layerName = hasFeaturesFromClusteredLayer ? feature.get('features')[0].get('layer') : feature.get("layer");
            
            if (layerName === "SailingDirectionsLayer") {
                const type = feature.get("featureType");
                const text = feature.get("text");
                const url = feature.get("url");

                if (type === "Book") {
                    books.push({ renderLine: () => html`<span class="tooltipUrl"><a href=${url}>${text}</a></span>`});
                }
                if (type === "BookChapter") {
                    chapters.push({ renderLine: () => html`<span class="tooltipUrl"><a href=${url}>${text}</a></span>`});
                }
            }

            if (layerName === "NavAreasLayer") {
                const text = feature.get("text");
                areas.push({ renderLine: () => html`<span>${text}</span>`});
            }
            if (layerName === "WhaleZonesLayer") {
                const text = feature.get("text");
                whaleZonesText.add(text)
            }
            if (layerName === "MarpolLayer") {
                const text = feature.get("text");
                marpol.push({ renderLine: () => html`<span>${text}</span>`});
            }
            if (layerName === "EcaSecaLayer") {
                const text = feature.get("text");
                ecaSeca.push({ renderLine: () => html`<span>${text}</span>`});
            }
            if (layerName === "BWMLayer") {
                const text = feature.get("text");
                bwmZones.push({ renderLine: () => html`<span>${text}</span>`});
            }
            if (layerName === "RestrictedAreasLayer") {
                const text = feature.get("text");
                restrictedAreas.push({ renderLine: () => html`<span>${text}</span>`});
            }
            if (layerName === "Navtex") {
                const address = feature.get("address");
                const designator = feature.get("designator");
                navtexStations.push({ address: address, renderLine: () => html`<div class="navtex-station">${address.replace(`NAVTEX / `, '')} ["${designator}"]</div>`});
            }
            if (layerName === "SecurityZonesLayer") {
                const text = feature.get("text");
                securityZones.push({ renderLine: () => html`<span>${text}</span>`});
            }
            if (layerName === "TerminalGeofenceLayer") {
                const text = feature.get("text");
                terminalGeofences.push({ renderLine: () => html`<span>${text}</span>`});
            }
        }

        const whaleZones = Array.from(whaleZonesText).map(el =>{ 
            return { renderLine: () => html`<span>${el}</span>`}
        });

        if (chapters.length > 0) {
            this._processInfo("Sailing directions", chapters);
        } else {
            this._processInfo("Sailing directions", books);
        }

        this._processInfo("Nav areas", areas);
        this._processInfo("Whale zones",whaleZones);
        this._processInfo("Marpol Areas", marpol);
        this._processInfo("ECA/SECA Areas", ecaSeca);
        this._processInfo("Navtex stations", navtexStations);
        this._processInfo("Ballast Water Management", bwmZones);
        this._processInfo('Restricted Areas', restrictedAreas);
        this._processInfo("Security Zones", securityZones);
        this._processInfo("Terminal Geofences", terminalGeofences);

        return true;
    }

    private _processInfo(source: TooltipSource, areas: TooltipText[]) {
        //trim overlapping text
        const trimmedAreas: TooltipText[] = [];
        const renderedText = new Set<string | undefined>();

        areas.forEach(area => {
            const text = area.renderLine()?.strings[0];
            if (!renderedText.has(text)) {
                renderedText.add(text);
                trimmedAreas.push(area);
            }
        });

        if (trimmedAreas.length > 0) {
            this.setTooltipText(source, trimmedAreas);
        } else {
            this.removeTooltipText(source);
        }
    }

    private _alignVesselCategoryNames(category: VesselCategories) {
        switch (category) {
            case VesselCategories.Maersk:
                return "Maersk operated vessels"
            case VesselCategories.NonMaersk:
                return "Non Maersk operated vessels"
            case VesselCategories.Alarm:
                return "Safety Alarm"
            case VesselCategories.Tug:
                return "Tugs"
            case VesselCategories.Tanker:
                return "Tankers"
            case VesselCategories.RoRo:
                return "Roro"
            case VesselCategories.BulkCarrier:
                return "Bulk carriers"
            default:
                return "Other"
        }
    }
};
